import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { OktaAuth } from '@okta/okta-auth-js'
import { Preferences } from '@capacitor/preferences'
import { Browser } from '@capacitor/browser'
import { isNative, platform, getManagedSecret, unregisterPushNotifications } from '../services/native'

const AuthContext = createContext(null)

// Decode JWT payload without verification (for offline expiry checks)
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

// Check if a fetch error is a network failure (vs a server error response)
function isNetworkError(error) {
  return error instanceof TypeError && error.message === 'Failed to fetch'
}

/**
 * Unreachable and answering-with-a-500 are the same thing as far as a session
 * is concerned: the server did not tell us the token is bad, so we must not
 * act as though it did. Only an explicit 401 means signed out.
 */
function isServerUnavailable(error) {
  return isNetworkError(error) || error?.serverUnavailable === true
}

// Device token persistence — Capacitor Preferences (native) + localStorage + cookie
function getDeviceToken() {
  let token = localStorage.getItem('pinDeviceToken')
  if (!token) {
    const match = document.cookie.match(/(?:^|; )pinDeviceToken=([^;]+)/)
    if (match) {
      token = match[1]
      localStorage.setItem('pinDeviceToken', token)
    }
  }
  return token
}

async function hydrateDeviceToken() {
  if (!isNative()) return
  try {
    const { value } = await Preferences.get({ key: 'pinDeviceToken' })
    if (value && !localStorage.getItem('pinDeviceToken')) {
      localStorage.setItem('pinDeviceToken', value)
    }
  } catch (e) {
    console.log('Preferences hydrate failed:', e)
  }
}

function setDeviceToken(token) {
  localStorage.setItem('pinDeviceToken', token)
  document.cookie = `pinDeviceToken=${token}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Strict; Secure`
  if (isNative()) {
    Preferences.set({ key: 'pinDeviceToken', value: token }).catch(() => {})
  }
}

function clearDeviceToken() {
  localStorage.removeItem('pinDeviceToken')
  document.cookie = 'pinDeviceToken=; path=/; max-age=0; SameSite=Strict; Secure'
  if (isNative()) {
    Preferences.remove({ key: 'pinDeviceToken' }).catch(() => {})
  }
}

// Role hierarchy: key implies all values (matches server/core/roles.js)
const ROLE_HIERARCHY = {
  super_admin: ['admin', 'incident_reports_admin', 'operations_admin', 'apparatus_admin', 'treasurer', 'personnel_admin', 'gear_admin'],
  admin: ['incident_reports_admin', 'operations_admin', 'apparatus_admin', 'treasurer', 'personnel_admin', 'gear_admin']
}

/**
 * Department admin, not "holds some role".
 *
 * This was `roles.length > 0` everywhere, so an apparatus_admin or a
 * gear_admin came back as isAdmin and the UI opened department-wide admin
 * controls to them. The server made the same mistake and has been corrected
 * the same way; these two must agree or the interface offers things the API
 * refuses.
 *
 * ROLE_HIERARCHY means super_admin and admin both satisfy this, and the module
 * roles do not.
 */
function isDepartmentAdmin(roles = []) {
  if (roles.includes('admin') || roles.includes('super_admin')) return true
  return roles.some((held) => ROLE_HIERARCHY[held]?.includes('admin'))
}

/**
 * Was this page opened by Okta to start a sign-in (OIDC third-party initiated
 * login)? Okta opens the app's "initiate login URI", /login, adding
 * ?iss=<its issuer>. Only our own Okta counts, so a link to /login?iss=...
 * elsewhere can't start anything but a normal sign-in to this department's Okta.
 */
/** An Okta error meaning the stored sign-in is dead (revoked, expired, invalid). */
export function isStaleOktaSession(error) {
  if (!error) return false
  const code = error.errorCode || error.error || ''
  if (['invalid_token', 'invalid_grant', 'login_required'].includes(code)) return true
  return /revoked|expired|invalid[_ ]token/i.test(String(error.message || error.errorSummary || ''))
}

export function isOktaInitiatedLogin(issuer, location = window.location) {
  if (!issuer || location.pathname !== '/login') return false
  const iss = new URLSearchParams(location.search).get('iss')
  // Compare origins: Okta may send the org URL even when the issuer is an
  // authorization server under it (/oauth2/default).
  const origin = (u) => { try { return new URL(u).origin.toLowerCase() } catch { return null } }
  return !!iss && origin(iss) !== null && origin(iss) === origin(issuer)
}

export function AuthProvider({ children }) {
  const [oktaAuth, setOktaAuth] = useState(null)
  const [oktaConfigured, setOktaConfigured] = useState(null) // null = loading, false = not configured, true = ready
  const [oktaEnabled, setOktaEnabled] = useState(() => {
    // Offline fallback: use cached value
    const cached = localStorage.getItem('cachedOktaEnabled')
    return cached === null ? null : cached === '1'
  })
  const [localAuthAvailable, setLocalAuthAvailable] = useState(false)
  // True when we could not ask the server what sign-in methods exist, as
  // distinct from being told there are none.
  const [authConfigFailed, setAuthConfigFailed] = useState(false)
  const [isPinDevice, setIsPinDevice] = useState(false)
  const [pinDeviceInfo, setPinDeviceInfo] = useState(null) // { deviceName, autoLogoutMinutes, sessionDurationHours }
  const [mfaPending, setMfaPending] = useState(false)
  const [mfaToken, setMfaToken] = useState(null)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false)
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    accessToken: null,
    isAdmin: false,
    groups: [],
    roles: [],
    isSuperAdmin: false,
    error: null,
    isLocalUser: false,
    forcePasswordChange: false
  })

  const tokenCheckInterval = useRef(null)
  const inactivityTimer = useRef(null)

  // Hierarchy-aware role check (mirrors server-side hasRole)
  const hasRole = useCallback((requiredRole) => {
    const userRoles = authState.roles || []
    if (userRoles.length === 0) return false

    // Direct match
    if (userRoles.includes(requiredRole)) return true

    // Check hierarchy
    for (const held of userRoles) {
      const implied = ROLE_HIERARCHY[held]
      if (implied && implied.includes(requiredRole)) return true
    }

    return false
  }, [authState.roles])

  // Fetch roles from backend (caches in localStorage for offline fallback)
  const fetchRoles = async (token) => {
    try {
      const res = await fetch('/api/auth/local/my-roles', {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await res.json()
      if (data.success) {
        const roles = data.roles || []
        localStorage.setItem('cachedRoles', JSON.stringify(roles))
        return roles
      }
    } catch (error) {
      if (isNetworkError(error)) {
        // Offline — return cached roles
        try {
          const cached = JSON.parse(localStorage.getItem('cachedRoles'))
          if (Array.isArray(cached)) return cached
        } catch {}
      }
      console.error('Failed to fetch roles:', error)
    }
    return []
  }

  // Check for existing local session
  const checkLocalSession = async () => {
    const token = localStorage.getItem('localAuthToken')
    if (!token) return false

    try {
      const res = await fetch('/api/auth/local/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
      // A 5xx is the server failing to answer, not the token being rejected.
      // Treating the two alike is what discarded a valid session on a
      // transient error and dropped the user on the login page. Throwing here
      // routes it to the offline branch below, which keeps the session when
      // the token itself is still within its expiry.
      if (res.status >= 500) {
        const err = new Error(`local/me ${res.status}`)
        err.serverUnavailable = true
        throw err
      }
      const data = await res.json()

      if (data.success && data.user) {
        // Fetch roles from backend
        const roles = await fetchRoles(token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            name: data.user.name,
            email: data.user.email
          },
          accessToken: token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: data.user.forcePasswordChange
        })
        return true
      }
    } catch (error) {
      // Offline, or the server erroring: decode JWT locally and keep session
      // if not expired. A 500 is not a rejection of the token.
      if (isServerUnavailable(error)) {
        const payload = decodeJwtPayload(token)
        if (payload?.exp && payload.exp > Date.now() / 1000) {
          const roles = (() => {
            try {
              const cached = JSON.parse(localStorage.getItem('cachedRoles'))
              return Array.isArray(cached) ? cached : []
            } catch { return [] }
          })()

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: { name: payload.name, email: payload.email },
            accessToken: token,
            isAdmin: isDepartmentAdmin(roles),
            groups: payload.groups || [],
            roles,
            isSuperAdmin: roles.includes('super_admin'),
            error: null,
            isLocalUser: true,
            forcePasswordChange: false
          })
          return true
        }
        // Token expired — clear it
        console.log('Offline and token expired, clearing session')
      } else {
        console.error('Local session check failed:', error)
      }
      localStorage.removeItem('localAuthToken')
    }
    return false
  }

  // Fetch Okta config and local auth status
  useEffect(() => {
    const initAuth = async () => {
      // Request persistent storage to prevent iPadOS from evicting PWA data
      if (navigator.storage?.persist) {
        navigator.storage.persist().catch(() => {})
      }

      // Restore device token from native persistent storage (survives Safari ITP)
      await hydrateDeviceToken()

      // Check if this is a registered station device
      let hasValidDevice = false
      const deviceToken = getDeviceToken()
      if (deviceToken) {
        try {
          const deviceRes = await fetch('/api/auth/pin/device-status', {
            headers: { 'X-Device-Token': deviceToken }
          })
          const deviceData = await deviceRes.json()
          if (deviceData.success && deviceData.valid) {
            hasValidDevice = true
            setIsPinDevice(true)
            setPinDeviceInfo({
              deviceName: deviceData.deviceName,
              autoLogoutMinutes: deviceData.autoLogoutMinutes,
              sessionDurationHours: deviceData.sessionDurationHours
            })
          } else {
            // Device token is invalid/revoked — clear it
            clearDeviceToken()
          }
        } catch (err) {
          // Offline — keep device token, assume still valid
          if (isNetworkError(err)) {
            hasValidDevice = true
            setIsPinDevice(true)
          }
          console.log('PIN device check failed:', err)
        }
      }

      // Auto-register managed device if no valid device token exists
      if (!hasValidDevice && isNative()) {
        try {
          const secret = await getManagedSecret()
          if (secret) {
            const autoRes = await fetch('/api/auth/pin/auto-register', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ managedSecret: secret })
            })
            const autoData = await autoRes.json()
            if (autoData.success) {
              setDeviceToken(autoData.deviceToken)
              setIsPinDevice(true)
              setPinDeviceInfo({
                deviceName: autoData.deviceName,
                autoLogoutMinutes: autoData.autoLogoutMinutes
              })
            }
          }
        } catch (err) {
          console.log('Auto-register failed:', err)
        }
      }

      // Check local auth status
      try {
        const localRes = await fetch('/api/auth/local/status')
        // fetch only rejects on a network failure, so a 503 would otherwise
        // parse cleanly and read as "no local accounts". The server returns
        // 503 when it could not determine the answer; that has to land in the
        // catch below with every other kind of failure.
        if (!localRes.ok) throw new Error(`local status ${localRes.status}`)
        const localData = await localRes.json()
        const available = localData.available && localData.hasUsers
        setLocalAuthAvailable(available)
        localStorage.setItem('cachedLocalAuthAvailable', available ? '1' : '0')
      } catch (error) {
        // Any failure, not just an offline one. A 502 from a cold start is not
        // a network error by this test, and treating it as 'no local accounts'
        // is how the login page ended up offering nothing at all.
        setLocalAuthAvailable(localStorage.getItem('cachedLocalAuthAvailable') === '1')
        setAuthConfigFailed(true)
        console.log('Local auth status check failed:', error)
      }

      // Fetch Okta config first (always — login page needs this even if a local session exists,
      // because if that session expires the user needs the Okta option to log back in)
      let oktaReady = false
      try {
        const res = await fetch('/api/okta/config')
        // Same reasoning as the local status probe above: a 503 means the
        // server could not read its configuration, which is not the same as
        // Okta being switched off, and must not be cached as though it were.
        if (!res.ok) throw new Error(`okta config ${res.status}`)
        const data = await res.json()
        console.log('Okta config response:', data)

        // Track oktaEnabled from backend
        if (data.oktaEnabled !== undefined) {
          setOktaEnabled(data.oktaEnabled)
          localStorage.setItem('cachedOktaEnabled', data.oktaEnabled ? '1' : '0')
        }

        // Remember the answer. A failed fetch previously fell through to
        // setOktaConfigured(false), which the login page renders exactly like
        // 'Okta is switched off' -- so a transient failure looked identical to
        // a deliberate configuration, and only a hard refresh cleared it.
        localStorage.setItem('cachedOktaConfigured', (data.success && data.configured) ? '1' : '0')

        if (data.success && data.configured) {
          if (isNative()) {
            // Native: use server-side OAuth flow, no Okta SDK needed
            setOktaConfigured(true)
            oktaReady = true
          } else {
            // Web: initialize OktaAuth SDK for browser-based redirect flow
            let issuer = data.domain.startsWith('https://') ? data.domain : `https://${data.domain}`

            const config = {
              issuer,
              clientId: data.clientId,
              redirectUri: `${window.location.origin}/`,
              postLogoutRedirectUri: `${window.location.origin}/`,
              scopes: ['openid', 'profile', 'email', 'groups'],
              pkce: true,
              tokenManager: {
                storage: 'localStorage',
                autoRenew: true,
                expireEarlySeconds: 120
              }
            }
            console.log('Initializing OktaAuth with config:', { ...config, clientId: '***' })

            try {
              const auth = new OktaAuth(config)
              setOktaAuth(auth)
              setOktaConfigured(true)
              oktaReady = true
              console.log('OktaAuth initialized successfully')
            } catch (oktaError) {
              console.error('Failed to initialize OktaAuth:', oktaError)
              setOktaConfigured(false)
            }
          }
        } else {
          console.log('Okta not configured:', data.message || 'No credentials')
          setOktaConfigured(false)
        }
      } catch (error) {
        console.error('Failed to fetch Okta config:', error)
        // Not the same as 'not configured'. Fall back to what we last knew, and
        // if we have never known, say the options could not be loaded rather
        // than claiming none exist.
        const cached = localStorage.getItem('cachedOktaConfigured')
        if (cached !== null) {
          setOktaConfigured(cached === '1')
        } else {
          setOktaConfigured(false)
        }
        setAuthConfigFailed(true)
      }

      // Check for existing local session
      const hasLocalSession = await checkLocalSession()
      if (hasLocalSession) {
        return
      }

      // No session — set loading false
      // On web with Okta SDK initialized, the second useEffect (initOktaAuth) handles it.
      // PIN devices skip waiting for Okta — users will authenticate via PIN, not SSO.
      if (!oktaReady || isNative() || hasValidDevice) {
        setAuthState(prev => ({ ...prev, isLoading: false }))
      }
    }

    initAuth()

    // On native, listen for deep link OAuth callback with JWT token (warm resume)
    // For cold start, the token is already in localStorage when checkLocalSession() runs above.
    if (isNative()) {
      const handleMobileAuth = () => checkLocalSession()
      window.addEventListener('mobileAuthReady', handleMobileAuth)
      return () => window.removeEventListener('mobileAuthReady', handleMobileAuth)
    }
  }, [])

  // Local login function
  const localLogin = useCallback(async (username, password) => {
    try {
      const res = await fetch('/api/auth/local/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })
      const data = await res.json()

      if (data.success) {
        // MFA enabled — store mfaToken in state only (not localStorage)
        if (data.mfaPending) {
          setMfaPending(true)
          setMfaToken(data.mfaToken)
          return { success: true, mfaPending: true }
        }

        localStorage.setItem('localAuthToken', data.token)

        // Fetch roles from backend
        const roles = await fetchRoles(data.token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            name: data.user.name,
            email: data.user.email
          },
          accessToken: data.token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: data.user.forcePasswordChange
        })

        // Department requires MFA but user hasn't set it up
        if (data.mfaSetupRequired) {
          setMfaSetupRequired(true)
        }

        return { success: true, forcePasswordChange: data.user.forcePasswordChange, mfaSetupRequired: data.mfaSetupRequired }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // MFA verify during login (uses mfaPending token)
  const verifyMfa = useCallback(async (code) => {
    try {
      const res = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${mfaToken}`
        },
        body: JSON.stringify({ token: code })
      })
      const data = await res.json()

      if (data.success) {
        localStorage.setItem('localAuthToken', data.token)
        const roles = await fetchRoles(data.token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: { name: data.user.name, email: data.user.email },
          accessToken: data.token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: data.user.forcePasswordChange
        })
        setMfaPending(false)
        setMfaToken(null)
        return { success: true, forcePasswordChange: data.user.forcePasswordChange }
      }
      return { success: false, error: data.error }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message }
    }
  }, [mfaToken])

  // MFA verify with backup code during login
  const verifyMfaBackup = useCallback(async (code) => {
    try {
      const res = await fetch('/api/auth/mfa/verify-backup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${mfaToken}`
        },
        body: JSON.stringify({ code })
      })
      const data = await res.json()

      if (data.success) {
        localStorage.setItem('localAuthToken', data.token)
        const roles = await fetchRoles(data.token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: { name: data.user.name, email: data.user.email },
          accessToken: data.token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: data.user.forcePasswordChange
        })
        setMfaPending(false)
        setMfaToken(null)
        return { success: true, backupCodesRemaining: data.backupCodesRemaining }
      }
      return { success: false, error: data.error }
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message }
    }
  }, [mfaToken])

  // Called after MFA setup is complete
  const completeMfaSetup = useCallback(() => {
    setMfaSetupRequired(false)
  }, [])

  // Cancel MFA verification (go back to login)
  const cancelMfa = useCallback(() => {
    setMfaPending(false)
    setMfaToken(null)
  }, [])

  // Change password function
  const changePassword = useCallback(async (username, currentPassword, newPassword) => {
    try {
      const res = await fetch('/api/auth/local/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, currentPassword, newPassword })
      })
      const data = await res.json()

      if (data.success) {
        localStorage.setItem('localAuthToken', data.token)
        setAuthState(prev => ({
          ...prev,
          accessToken: data.token,
          forcePasswordChange: false
        }))
        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // PIN login function (for station device quick login)
  const pinLogin = useCallback(async (userIdentifier, pin) => {
    const deviceToken = getDeviceToken()
    if (!deviceToken) {
      return { success: false, error: 'Device not registered' }
    }

    try {
      const res = await fetch('/api/auth/pin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Device-Token': deviceToken
        },
        body: JSON.stringify({ userIdentifier, pin })
      })
      const data = await res.json()

      if (data.success) {
        localStorage.setItem('localAuthToken', data.token)

        // Fetch roles from backend
        const roles = await fetchRoles(data.token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            name: data.user.name,
            email: data.user.email
          },
          accessToken: data.token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: false,
          authMethod: 'pin'
        })

        // Start inactivity auto-logout timer
        if (data.autoLogoutMinutes > 0) {
          startInactivityTimer(data.autoLogoutMinutes)
        }

        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // Biometric login — exchanges biometric token for a fresh JWT session
  const biometricLogin = useCallback(async (token, deviceId) => {
    try {
      const res = await fetch('/api/auth/biometric/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, deviceId })
      })
      const data = await res.json()

      if (data.success) {
        localStorage.setItem('localAuthToken', data.token)

        const roles = await fetchRoles(data.token)

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: {
            name: data.user.name,
            email: data.user.email
          },
          accessToken: data.token,
          isAdmin: isDepartmentAdmin(roles),
          groups: data.user.isAdmin ? ['Local Admins'] : [],
          roles,
          isSuperAdmin: roles.includes('super_admin'),
          error: null,
          isLocalUser: true,
          forcePasswordChange: false
        })
        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // Enroll biometric — creates server-side token and returns plaintext for device storage
  // Read token directly from localStorage to avoid stale closure over getAccessToken
  // (biometric enrollment is native-only, where tokens are always in localStorage)
  const enrollBiometric = useCallback(async (deviceId) => {
    try {
      const token = localStorage.getItem('localAuthToken')
      const res = await fetch('/api/auth/biometric/enroll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ deviceId })
      })
      const data = await res.json()

      if (data.success) {
        return { success: true, token: data.token, expiresAt: data.expiresAt }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // Register station device
  const registerPinDevice = useCallback(async (code, managedSecret = null) => {
    try {
      const body = { code }
      if (managedSecret) body.managedSecret = managedSecret

      const res = await fetch('/api/auth/pin/register-device', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const data = await res.json()

      if (data.success) {
        setDeviceToken(data.deviceToken)
        setIsPinDevice(true)
        setPinDeviceInfo({
          deviceName: data.deviceName,
          autoLogoutMinutes: data.autoLogoutMinutes
        })
        return { success: true }
      } else {
        return { success: false, error: data.error }
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }, [])

  // Inactivity auto-logout for station devices
  const startInactivityTimer = useCallback((minutes) => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    if (!minutes || minutes <= 0) return

    const ms = minutes * 60 * 1000
    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
      inactivityTimer.current = setTimeout(() => {
        // Auto-logout
        localStorage.removeItem('localAuthToken')
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          isAdmin: false,
          groups: [],
          roles: [],
          isSuperAdmin: false,
          error: null,
          isLocalUser: false,
          forcePasswordChange: false
        })
      }, ms)
    }

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }))
    resetTimer()

    // Cleanup on unmount handled in logout
  }, [])

  // Get the best available token (prefer ID token for Okta free tier)
  const getBestToken = useCallback(() => {
    // For local users, return the stored token
    if (authState.isLocalUser) {
      return localStorage.getItem('localAuthToken')
    }
    if (!oktaAuth) return null
    const idToken = oktaAuth.getIdToken()
    if (idToken) return idToken
    return oktaAuth.getAccessToken()
  }, [oktaAuth, authState.isLocalUser])

  // Check if token is expired or about to expire
  const isTokenExpired = useCallback(() => {
    if (isNative() || authState.isLocalUser) {
      // Native always uses local JWTs; server validates on each request
      return false
    }
    if (!oktaAuth) return true
    const tokens = oktaAuth.tokenManager.getTokensSync()
    if (!tokens || (!tokens.accessToken && !tokens.idToken)) {
      return true
    }

    const now = Math.floor(Date.now() / 1000)
    const buffer = 60

    if (tokens.idToken?.expiresAt) {
      if (tokens.idToken.expiresAt - buffer <= now) {
        return true
      }
    }

    if (tokens.accessToken?.expiresAt) {
      if (tokens.accessToken.expiresAt - buffer <= now) {
        return true
      }
    }

    return false
  }, [oktaAuth, authState.isLocalUser])

  // Refresh tokens
  const refreshTokens = useCallback(async () => {
    if (isNative() || authState.isLocalUser) return true // Native/local tokens don't need refresh
    if (!oktaAuth) return false

    // Check if tokens exist before trying to renew
    const existingIdToken = await oktaAuth.tokenManager.get('idToken')
    const existingAccessToken = await oktaAuth.tokenManager.get('accessToken')

    if (!existingIdToken && !existingAccessToken) {
      // No tokens to refresh - user not logged in
      return false
    }

    try {
      if (existingIdToken) {
        await oktaAuth.tokenManager.renew('idToken')
      }
      if (existingAccessToken) {
        await oktaAuth.tokenManager.renew('accessToken')
      }
      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      return false
    }
  }, [oktaAuth, authState.isLocalUser])

  // Handle token expiration
  const handleTokenExpiration = useCallback(async () => {
    if (isNative() || authState.isLocalUser) return true

    const refreshed = await refreshTokens()
    if (refreshed) {
      const newToken = getBestToken()
      setAuthState(prev => ({ ...prev, accessToken: newToken }))
      return true
    }

    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      isAdmin: false,
      groups: [],
      roles: [],
      isSuperAdmin: false,
      error: 'Session expired. Please log in again.',
      isLocalUser: false,
      forcePasswordChange: false
    })

    return false
  }, [refreshTokens, getBestToken, authState.isLocalUser])

  // Process Okta OAuth callback (code= in URL or pending in sessionStorage)
  const processOktaCallback = useCallback(async () => {
    if (!oktaAuth) return false

    // Check sessionStorage for deep-link callback params (native app returning from browser)
    if (!window.location.search.includes('code=')) {
      try {
        const pending = sessionStorage.getItem('pendingOAuthCallback')
        if (pending) {
          const { code, state } = JSON.parse(pending)
          if (code && state) {
            window.history.replaceState({}, '', `/?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`)
          }
          sessionStorage.removeItem('pendingOAuthCallback')
        }
      } catch (e) {
        console.log('No pending OAuth callback')
      }
    }

    if (!window.location.search.includes('code=')) return false

    try {
      await oktaAuth.handleLoginRedirect()
    } catch (callbackError) {
      // PKCE codeVerifier missing - auth flow already completed or was interrupted
      console.log('OAuth callback already processed or expired, checking existing session')
    }
    window.history.replaceState({}, document.title, window.location.pathname)
    return true
  }, [oktaAuth])

  // Initialize Okta auth state after Okta is configured (web only — native uses server-side flow)
  useEffect(() => {
    if (!oktaAuth || oktaConfigured !== true || authState.isLocalUser || isNative()) return

    // Skip Okta flow if there's a local JWT — checkLocalSession() in the first
    // useEffect will handle it. This prevents a race where the Okta flow resolves
    // before checkLocalSession() and briefly shows the login screen.
    if (localStorage.getItem('localAuthToken')) return

    // PIN devices don't need Okta — they authenticate via PIN
    if (isPinDevice) return

    const initOktaAuth = async () => {
      try {
        // Handle OAuth callback
        await processOktaCallback()

        const isAuth = await oktaAuth.isAuthenticated()

        if (isAuth) {
          if (isTokenExpired()) {
            const refreshed = await refreshTokens()
            if (!refreshed) {
              setAuthState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                accessToken: null,
                isAdmin: false,
                groups: [],
                roles: [],
                isSuperAdmin: false,
                error: 'Session expired. Please log in again.',
                isLocalUser: false,
                forcePasswordChange: false
              })
              return
            }
          }

          const user = await oktaAuth.getUser()
          const token = getBestToken()
          const groups = user.groups || []

          // Fetch roles from backend
          const roles = await fetchRoles(token)

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            accessToken: token,
            isAdmin: isDepartmentAdmin(roles),
            groups,
            roles,
            isSuperAdmin: roles.includes('super_admin'),
            error: null,
            isLocalUser: false,
            forcePasswordChange: false
          })
        } else if (isOktaInitiatedLogin(oktaAuth.options.issuer)) {
          // Launched from the Okta dashboard: Okta sends the browser to
          // /login?iss=<issuer> (its "initiate login URI"). Start the sign-in
          // ourselves instead of waiting for the button; with an Okta session
          // already open it comes straight back signed in. Loading stays on
          // while the browser leaves.
          await oktaAuth.signInWithRedirect()
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        // Tokens Okta has revoked or that have lapsed still look valid locally
        // until getUser() asks Okta, which rejects them ("The access token has
        // been revoked."). They can never work again: drop them, and carry on
        // as signed out -- including the Okta-dashboard launch, which would
        // otherwise stop here.
        const stale = isStaleOktaSession(error)
        if (stale) {
          try { oktaAuth.tokenManager.clear() } catch { /* nothing stored */ }
          if (isOktaInitiatedLogin(oktaAuth.options.issuer)) {
            try { await oktaAuth.signInWithRedirect(); return } catch { /* fall through */ }
          }
        }
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          isAdmin: false,
          groups: [],
          roles: [],
          isSuperAdmin: false,
          error: stale ? null : error.message,
          isLocalUser: false,
          forcePasswordChange: false
        })
      }
    }

    initOktaAuth()

    // Set up token event listeners
    oktaAuth.tokenManager.on('renewed', (key) => {
      if (key === 'idToken' || key === 'accessToken') {
        const token = getBestToken()
        setAuthState(prev => ({ ...prev, accessToken: token }))
      }
    })

    oktaAuth.tokenManager.on('expired', () => {
      handleTokenExpiration()
    })

    oktaAuth.tokenManager.on('error', (error) => {
      // Ignore "no token" errors - these are expected when user isn't logged in
      if (error?.message?.includes('no token')) return
      console.error('Token manager error:', error)
    })

    // Periodic token check - only run if user is authenticated
    tokenCheckInterval.current = setInterval(async () => {
      // Skip if not authenticated or no tokens exist
      const existingToken = await oktaAuth.tokenManager.get('idToken')
      if (!existingToken) return

      if (isTokenExpired()) {
        handleTokenExpiration()
      }
    }, 30000)

    return () => {
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current)
      }
      oktaAuth.tokenManager.off('renewed')
      oktaAuth.tokenManager.off('expired')
      oktaAuth.tokenManager.off('error')
    }
  }, [oktaAuth, oktaConfigured, authState.isLocalUser, isTokenExpired, refreshTokens, getBestToken, handleTokenExpiration, processOktaCallback])

  const login = useCallback(async () => {
    if (isNative()) {
      const serverUrl = localStorage.getItem('scenelightServerUrl')
      if (!serverUrl) {
        setAuthState(prev => ({ ...prev, error: 'Not connected to a department' }))
        return
      }
      await Browser.open({ url: `${serverUrl}/api/auth/okta/mobile-start` })
      return
    }

    // Web: use Okta SDK redirect flow
    if (!oktaAuth) {
      console.error('Login failed: oktaAuth not initialized')
      setAuthState(prev => ({ ...prev, error: 'Okta authentication not initialized. Please check Okta configuration.' }))
      return
    }
    try {
      await oktaAuth.signInWithRedirect()
    } catch (error) {
      console.error('Login error:', error)
      setAuthState(prev => ({ ...prev, error: error.message }))
    }
  }, [oktaAuth])

  const logout = useCallback(async () => {
    // Hand this device's push token back first, while still signed in, so a
    // shared iPad stops getting the signed-out member's notifications.
    await unregisterPushNotifications()

    // Biometric credentials are intentionally preserved across logouts —
    // they allow the user to sign back in with fingerprint/face without re-enrolling.
    // Biometric tokens are only revoked when the user explicitly disables biometric
    // login or when the 90-day token expires server-side.

    // Clear local auth (but keep pinDeviceToken — device stays registered)
    localStorage.removeItem('localAuthToken')
    localStorage.removeItem('cachedRoles')

    // Clear MFA state
    setMfaPending(false)
    setMfaToken(null)
    setMfaSetupRequired(false)

    // Clear inactivity timer
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current)
      inactivityTimer.current = null
    }

    // Clear interval first
    if (tokenCheckInterval.current) {
      clearInterval(tokenCheckInterval.current)
      tokenCheckInterval.current = null
    }

    // Clear auth state immediately to prevent error messages from token revocation
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      isAdmin: false,
      groups: [],
      roles: [],
      isSuperAdmin: false,
      error: null,
      isLocalUser: false,
      forcePasswordChange: false
    })

    if (authState.isLocalUser || !oktaAuth) {
      return
    }

    try {
      // Redirect through Okta's /logout endpoint to kill the SSO session cookie,
      // then Okta redirects back to postLogoutRedirectUri (configured at init).
      // Without this, the Okta session persists and re-login is automatic.
      await oktaAuth.signOut({
        postLogoutRedirectUri: `${window.location.origin}/`,
        revokeAccessToken: true,
        revokeRefreshToken: true
      })
    } catch (error) {
      // If signOut redirect fails, at least clear tokens locally
      oktaAuth.tokenManager.clear()
      console.log('Logout completed with note:', error.message)
    }
  }, [oktaAuth, authState.isLocalUser])

  const getAccessToken = useCallback(() => {
    // On native, tokens are always local JWTs in localStorage — bypass Okta logic entirely.
    // isNative() is a direct Capacitor call (not React state), so it's immune to stale closures.
    if (isNative() || authState.isLocalUser) {
      return localStorage.getItem('localAuthToken')
    }
    if (isTokenExpired()) {
      handleTokenExpiration()
    }
    return getBestToken()
  }, [authState.isLocalUser, isTokenExpired, getBestToken, handleTokenExpiration])

  // Allow external update of oktaEnabled (e.g., from OktaSettingsPage toggle)
  const updateOktaEnabled = useCallback((enabled) => {
    setOktaEnabled(enabled)
    localStorage.setItem('cachedOktaEnabled', enabled ? '1' : '0')
  }, [])

  const value = {
    ...authState,
    oktaConfigured,
    authConfigFailed,
    oktaEnabled,
    updateOktaEnabled,
    localAuthAvailable,
    isPinDevice,
    pinDeviceInfo,
    mfaPending,
    mfaSetupRequired,
    hasRole,
    login,
    localLogin,
    pinLogin,
    biometricLogin,
    enrollBiometric,
    registerPinDevice,
    changePassword,
    verifyMfa,
    verifyMfaBackup,
    completeMfaSetup,
    cancelMfa,
    logout,
    getAccessToken,
    oktaAuth,
    refreshTokens,
    isTokenExpired
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
